import * as React from "react";

import {userLevelCanSeePrices}                         from "@Common/logic/check-show-pricing";
import {isPickPack}                                    from "@Common/logic/item/is-pick-pack";
import {ComponentLink, ComponentNext, Link, makeChain} from "@Common/react/cst2";
import {downloadProofs}                                from "@Common/tools/cc-integration/common/download-proofs";
import {Types}                                         from "@Common/types";
import {buildUrlQuery}                                 from "@Common/utils/build-url-query";
import {chain}                                         from "@Common/utils/fs";
import {capitalize, upperCase1}                        from "@Common/utils/strings";

import {consumeContext}                            from "@Xpresso/loaders/consume-context";
import {PROOF_ENGINE_CC, PROOF_ENGINE_XPRESSPROOF} from "@Xpresso/routes/common/logic/proof-engine-type";
import {AGENT_LEVEL, COMPANY_LEVEL}                from "@Xpresso/routes/common/logic/user-level";
import {formatServiceLevelName}                    from "@Xpresso/routes/customize/steps/checkout/shipping-section/common/service-level/format-service-level-name";

import {NavigationService}        from "../../navigation-service";
import {OrderDetailsSidebar}      from "../common/order-details/order-details-sidebar";
import {ThumbnailPreviewModal}    from "../common/thumbnail-preview-modal/thumbnail-preview-modal";
import {ApprovalRequest}          from "./approval-requests";
import {ApproveConfirmModal}      from "./common/approve-confirm-modal/approve-confirm-modal";
import {DenialConfirmationModal}  from "./common/denial-confirmation-modal/denial-confirmation-modal";
import {DenialSuccessModal}       from "./common/denial-success-modal/denial-success-modal";
import {mapAppliedPaymentsAsList} from "./common/payments";

export const ATTRIBUTE_KEY_LABELS:{[Key in ApprovalRequestAttribute['key']]: string} = {
    agent           : "User",
    approve         : "Approve",
    branch          : "Office",
    corrupted       : "Order Corrupted",
    date            : "Date",
    delivery        : "Service Level",
    deny            : "Deny Cart",
    email           : "User Email",
    goToCart        : "View Cart",
    hasFastShipping : "Expedited Shipping",
    hasPromoCode    : "Promo Code",
    id              : "ID",
    price           : "Price",
};

export const ORDER_ATTRIBUTE_KEY_LABELS:{[Key in ApprovalRequestOrderAttribute['key']]: string} = {
    corrupted       : "Order Corrupted",
    deny            : "Deny Order",
    downloadPDF     : "PDF",
    downloadProof   : "Download",
    edit            : "Edit Order",
    kit             : "Kit",
    previewProof    : "Preview",
    price           : "Price",
    showDetails     : "Order Details",
};

export const SHIPPING_SERVICE_LEVELS_SORTED = [
    "PRIORITY_OVERNIGHT",
    "SATURDAY_DELIVERY",
    "STANDARD_OVERNIGHT",
    "FEDEX_2_DAY",
];

export type ApprovalRequestAttribute = (
    | {key: "approve", label: string, run: () => Promise<{cartId: number, submitToNextApprover: boolean, msg: string}>}
    | {key: "agent", goToAssets?: () => Promise<void>, isBranchOrder: boolean, value?: {idAgent: string, name: string}}
    | {key: "branch", goToAssets: () => Promise<void>, value: {idBranch: string, name: string}}
    | {key: "corrupted", message: string}
    | {key: "date", value: Date}
    | {key: "delivery", value: string}
    | {key: "deny", run: () => Promise<void>}
    | {key: "email", value: string}
    | {key: "goToCart", run: () => Promise<void>}
    | {key: "hasPromoCode", code: string, expires: Date}
    | {key: "hasFastShipping", level: string}
    | {key: "id", label: string, value: string}
    | {key: "price", breakdown: Record<"salesTax" | "handlingFee" | "shippingCharge" | "subTotal", number>, value: number}
    | {key: "rdfDescription", value: string}
);

export type ApprovalRequestAttributesDict = {
    [Key in ApprovalRequestAttribute['key']]?: Extract<ApprovalRequestAttribute, {key: Key}>;
};

export type ApprovalRequestOrderAttribute = (
    | {key: "corrupted", message: string}
    | {key: "deny", callToAction?: string, run: () => Promise<void>}
    | {key: "downloadPDF", loadUrl: () => Promise<{available: boolean, message?: string, url?: string}>, run: () => Promise<void>}
    | {key: "downloadProof", loadUrls: () => Promise<{available: boolean, urls?: any}>, run: () => Promise<void>}
    | {key: "edit", callToAction?: string, run: () => Promise<void>}
    | {key: "kit", components: Array<{idTemplate: number} & Types.Apis.Models.KitComponent>, isKit: boolean}
    | {key: "previewProof", run: () => Promise<void>}
    | {key: "price", value: number}
    | {key: "showDetails", run: () => Promise<void>}
);

export type ApprovalRequestOrderAttributesDict = {
    [Key in ApprovalRequestOrderAttribute['key']]?: Extract<ApprovalRequestOrderAttribute, {key: Key}>;
};

/**
 * Provides available details and actions for an approval request and its order(s)
 */
export const ApprovalRequestAttributesService = ({
    approvalAttributeDecorator  : decorateApprovalAttribute = ({attribute}) => attribute,
    orderAttributeDecorator     : decorateOrderAttribute    = ({attribute}) => attribute,
    renderChildren,
}:{
    approvalAttributeDecorator  ?: <Key extends ApprovalRequestAttribute['key']>(entry:{approval: ApprovalRequest, attribute: Extract<ApprovalRequestAttribute, {key: Key}>}) => Extract<ApprovalRequestAttribute, {key: Key}>,
    orderAttributeDecorator     ?: (entry:{approval: ApprovalRequest, order: ApprovalRequestOrder, attribute: ApprovalRequestOrderAttribute}) => ApprovalRequestOrderAttribute,
    renderChildren               : Link<JSX.Element, {
        makeOrderAttributes             : (approvalRequest:ApprovalRequest, order:Types.Utils.ArrayItem<ApprovalRequest['orders']>) => ApprovalRequestOrderAttributesDict,
        mapApprovalRequestAsAttributes  : (approvalRequest:ApprovalRequest) => ApprovalRequestAttributesDict,
    }>
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("session"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("routerHistory"))
    .then("navigation", ({}, next:ComponentLink<typeof NavigationService, "renderChildren">) => (
        NavigationService({renderChildren: next})
    ))

    .then("approveConfirmModal", ({}, next:ComponentLink<typeof ApproveConfirmModal>) => ApproveConfirmModal({next}))
    .then("denialConfirmationModal", ({}, next:ComponentLink<typeof DenialConfirmationModal>) => DenialConfirmationModal({next}))
    .then("denialSuccessModal", ({}, next:ComponentLink<typeof DenialSuccessModal>) => DenialSuccessModal({next}))

    .then("orderDetailsSidebar", ({}, next:ComponentLink<typeof OrderDetailsSidebar>) => OrderDetailsSidebar({next}))
    .then("thumbnailPreviewModal", ({}, next:ComponentLink<typeof ThumbnailPreviewModal, "renderSibling">) => ThumbnailPreviewModal({renderSibling: next}))

    .then(({
        apis,
        approveConfirmModal,
        company,
        denialConfirmationModal,
        denialSuccessModal,
        navigation,
        orderDetailsSidebar,
        session,
        thumbnailPreviewModal,
        userLevels: {userLevel},
        routerHistory,
    }) => renderChildren({
        mapApprovalRequestAsAttributes: (approval) => {
            const {appliedCredits, appliedPayments, approvalGate, id, orders, orders: [order]} = approval;

            if (order.orderCorrupt || ("corruptMessage" in order && order.corruptMessage)) {
                return {
                    corrupted   : {
                        key     : "corrupted",
                        message : "corruptMessage" in order ? order.corruptMessage : null,
                    },
                };
            }

            const attributes:ApprovalRequestAttribute[] = [
                {
                    key     : "date",
                    value   : new Date(
                        (orders as Array<ApprovalRequest['orders'][number]>)
                            .reduce((orderA, orderB) => orderA.dateModified > orderB.dateModified ? orderA : orderB)
                            .dateModified
                    )
                },
                {
                    key     : "delivery",
                    value   : (
                        order.deliveryType === "shipping"
                            ? formatServiceLevelName(order.deliveryService)
                            : `${order.deliveryService} Class`
                    )
                },
                {
                    key     : "email",
                    value   : order.usageEmail,
                },
                {
                    key     : "id",
                    label   : approvalGate === "cart" ? "Cart ID" : "Order ID",
                    value   : id.toString(),
                },
            ];

            if (canApproveRequest({approval})) {
                attributes.push(makeApprovalApproveAttribute({apis, approval, approveConfirmModal, session, userLevel, routerHistory}));
            }

            if (userLevel !== AGENT_LEVEL) {
                const isBranchOrder = order.agentName === "Branch Order";
                attributes.push({
                    key         : "agent",
                    goToAssets  : !isBranchOrder ? () => navigation.goToUserAssets({idAgent: order.idAgent, idBranch: order.idBranch}) : null,
                    value       : !isBranchOrder ? {
                        idAgent : order.idAgent,
                        name    : order.agentName,
                    } : null,
                    isBranchOrder,
                });
            }

            if (userLevel === COMPANY_LEVEL) {
                attributes.push({
                    key         : "branch",
                    goToAssets  : async () => navigation.goToOfficeAssets({idBranch: order.idBranch}),
                    value       : {
                        idBranch    : order.idBranch,
                        name        : order.branchName,
                    },
                });
            }

            if (approvalGate === "cart") {
                attributes.push({
                    key         : "deny",
                    run         : makeDenialLauncher({apis, approval, denialConfirmationModal, denialSuccessModal}),
                });

                attributes.push({
                    key         : "goToCart",
                    run         : async () => {
                        await session.applyEmulation({branchId: order.idBranch, agentId: order.idAgent === "Branch Order" ? undefined : order.idAgent});
                        if ("apm2EventId" in order) {
                            window.location.href = `/apm/home?event_id=${order.apm2EventId}&referrer=approval-requests`;
                        }
                        else {
                            routerHistory.push(`/checkout/${order.idCart}?referrer=approval-requests`)
                        }
                    },
                });
            }

            if (appliedCredits?.promoCodes?.[0]) {
                const promoCode = appliedCredits.promoCodes[0];
                attributes.push({
                    key     : "hasPromoCode",
                    code    : promoCode.code,
                    expires : new Date(promoCode.expires),
                });
            }

            if (orders.some((order:ApprovalRequestOrder) => SHIPPING_SERVICE_LEVELS_SORTED.includes(order.deliveryService))) {
                attributes.push({
                    key: "hasFastShipping",
                    level: SHIPPING_SERVICE_LEVELS_SORTED.at(
                        // get lowest (fastest service) index
                        (orders as typeof orders[number][]).reduce((level, order) => (
                            Math.min(level, Math.max(0, SHIPPING_SERVICE_LEVELS_SORTED.indexOf(order.deliveryService)))
                        ), Infinity)
                    ),
                });
            }

            if (approvalGate !== "proof" && (userLevelCanSeePrices({company, userLevel}) || appliedPaymentsShouldShowPrice(appliedPayments))) {
                const breakdown: any = {
                    salesTax            : (orders as Array<typeof orders[number]>).reduce(
                        (price, order) => price + (order.salesTax ?? 0),
                        0
                    ),
                    handlingFee         : (orders as Array<typeof orders[number]>).reduce(
                        (price, order) => price + (order.handlingFee ?? 0),
                        0
                    ),
                    shippingCharge      : (orders as Array<typeof orders[number]>).reduce(
                        (price, order) => price + (order.shippingCharge ?? 0),
                        0
                    ),
                    subTotal            : (orders as Array<typeof orders[number]>).reduce(
                        (price, order) => price + (order.subTotal ?? 0),
                        0
                    ),
                };

                const vatTax = (orders as Array<typeof orders[number]>).reduce(
                    (price, order) => price + (order.vatTax ?? 0),
                    0
                )
                const importDuties = (orders as Array<typeof orders[number]>).reduce(
                    (price, order) => price + (order.importDuties ?? 0),
                    0
                )
                if(vatTax > 0 || importDuties > 0) {
                    breakdown.vatTax = vatTax;
                    breakdown.importDuties = importDuties;
                }

                const rdfTotal = (orders as Array<typeof orders[number]>).reduce(
                    (price, order) => price + (order.rdf ?? 0),
                    0
                )
                if(rdfTotal > 0) {
                    const rdfLabel = (orders as Array<typeof orders[number]>).map(order => order.rdfDetails?.label).find(label => label !== undefined);
                    breakdown[rdfLabel] = rdfTotal;

                    const rdfDescription = (orders as Array<typeof orders[number]>).map(order => order.rdfDetails?.description).find(description => description !== undefined);
                    attributes.push({
                        key         : "rdfDescription",
                        value       : rdfDescription,
                    });
                }
                attributes.push({
                    breakdown,
                    key         : "price",
                    value       : breakdown.salesTax + breakdown.handlingFee + breakdown.subTotal + breakdown.shippingCharge + rdfTotal + vatTax + importDuties,
                });
            }

            const decoratedAttributes = attributes.map(attribute => decorateApprovalAttribute({approval, attribute})).filter(Boolean);
            return Object.fromEntries(decoratedAttributes.map(attribute => [attribute.key, attribute]));
        },

        makeOrderAttributes     : (approval, order) => {
            if (order.orderCorrupt) {
                return {
                    corrupted: {
                        key     : "corrupted",
                        message : "corruptMessage" in order ? order.corruptMessage : null,
                    },
                };
            }

            const {appliedCredits, appliedPayments, approvalGate, approvalType, orders, shipping} = approval;
            const attributes:ApprovalRequestOrderAttribute[] = [
                !isPickPack(order) && !orderHasComponents(order) && chain(
                    async () => {
                        let orderPdf:any;
                        if (approvalGate === "cart") {
                            if(order?.metaData?.pdfValidation?.pdfUrl) {
                                return {
                                    available : true,
                                    url       : order.metaData.pdfValidation.pdfUrl,
                                };
                            } else {
                                const cartPdfs  = await apis.next.cart.getpdfs(approval.id);
                                orderPdf        = cartPdfs.find(({url}) => url.includes(order.idOrder));
                            }
                        } else {
                            orderPdf = await apis.next.legacyOrder.getPreviewUrl(order.idOrder);
                        }

                        if (!orderPdf) {
                            return {available: false, message: "PDF unavailable"};
                        } else {
                            return {
                                available   : !("errors" in orderPdf),
                                message     : orderPdf.errors?.msg,
                                url         : orderPdf.url,
                            };
                        }
                    },
                    (loadUrl) => ({
                        loadUrl,
                        key     : "downloadPDF",
                        run     : async () => {
                            const result = await loadUrl();
                            if (result.available) {
                                window.open(result.url, "_blank");
                            } else {
                                window.alert(result.message ?? "PDF unavailable");
                            }
                        },
                    })
                ),
                {
                    key         : "kit",
                    isKit       : orderHasComponents(order),
                    components  : orderHasComponents(order) ? mapComponents(order.components) : [],
                },
            ];

            if (approvalGate !== "cart" || orders.length > 1) {
                attributes.push({
                    key             : "deny",
                    callToAction    : orderHasComponents(order) ? "Deny Kit Order" : null,
                    run             : makeDenialLauncher({apis, approval, denialConfirmationModal, denialSuccessModal, order}),
                });
            }

            if (approvalGate === "cart" && order.proofEngineType === PROOF_ENGINE_CC) {
                const loadUrls = async () => {
                    const cart      = await apis.next.cart.getCart(approval.id);
                    const proofUrls = cart.items.find(item => item.itemId === order.idOrder)?.metaData?.ccProofUrls;
                    return {available: !!proofUrls, urls: proofUrls};
                };
                attributes.push({
                    loadUrls,
                    key         : "downloadProof",
                    async run() {
                        const result = await loadUrls();
                        if (result.available) {
                            downloadProofs(result.urls, order.idOrder, Object.keys(result.urls))
                        } else {
                            window.alert("Proof unavailable");
                        }
                    },
                });
            }

            if (approvalType === "xd") {
                attributes.push({
                    key             : "edit",
                    callToAction    : orderHasComponents(order) ? "Edit Kit Order" : null,
                    run             : makeOrderEditLauncher({approvalGate, company, order, session, routerHistory}),
                });
            }

            if ((approvalGate !== "proof" || appliedPaymentsShouldShowPrice(approval.appliedPayments)) && "subTotal" in order) {
                attributes.push({
                    key     : "price",
                    value   : order.subTotal + order.handlingFee + order.salesTax + order.shippingCharge + order.rdf + order.vatTax + order.importDuties,
                });
            }

            if (approvalGate !== "proof") {
                attributes.push({
                    key     : "showDetails",
                    run     : async () => {
                        orderDetailsSidebar.show({
                            orderDetails    : (
                                approvalGate === "order" && "appliedPayments" in order
                                    ? mapStandaloneOrderAsDetails(order)
                                    : makeCartOrderDetails({appliedPayments, appliedCredits, shipping, order: order as Types.Apis.Models.OrderPendingApproval})
                            ),
                            renderFooter    : (
                                canApproveRequest({approval}) && orders.length === 1
                                    ? chain(
                                        decorateApprovalAttribute({approval, attribute: makeApprovalApproveAttribute({apis, approval, approveConfirmModal, session, userLevel, routerHistory})}),
                                        (approve) => ({close}) => (
                                            <div className="approval-requests-order-details-sidebar-footer-54455">
                                                <button onClick={() => approve.run().then(close)}>{approve.label}</button>
                                            </div>
                                        ),
                                    )
                                    : null
                            ),
                        });
                    }
                });
            }

            if (!isPickPack(order) && !orderHasComponents(order)) {
                attributes.push({
                    key     : "previewProof",
                    run     : async () => thumbnailPreviewModal.show(order.itemThumbnails.map(url => ({url}))),
                });

                const loadUrl = async () => {
                    let orderPdf:any;
                    if (approvalGate === "cart") {
                        if(order?.metaData?.pdfValidation?.pdfUrl) {
                            return {
                                available : true,
                                url       : order.metaData.pdfValidation.pdfUrl,
                            };
                        } else if(order?.combinedPdfWM) {
                            return {
                                available : true,
                                url       : order.combinedPdfWM
                            };
                        } else {
                            const cartPdfs  = await apis.next.cart.getpdfs(approval.id);
                            orderPdf        = cartPdfs.find(({url}) => url.includes(order.idOrder));
                        }
                    } else {
                        orderPdf = await apis.next.legacyOrder.getPreviewUrl(order.idOrder);
                    }

                    if (!orderPdf) {
                        return {available: false, message: "PDF unavailable"};
                    } else {
                        return {
                            available   : !("errors" in orderPdf),
                            message     : orderPdf.errors?.msg,
                            url         : orderPdf.url,
                        };
                    }
                };
                attributes.push({
                    loadUrl,
                    key     : "downloadPDF",
                    run     : async () => {
                        const result = await loadUrl();
                        if (result.available) {
                            window.open(result.url, "_blank");
                        } else {
                            window.alert(result.message ?? "PDF unavailable");
                        }
                    },
                });
            }

            const decoratedAttributes = attributes.map(attribute => decorateOrderAttribute({approval, attribute, order})).filter(Boolean);
            return Object.fromEntries(decoratedAttributes.map(attribute => [attribute.key, attribute]));
        },
    }))
    .run()
);

export const isApprovalAttributeOfKey = <Key extends ApprovalRequestAttribute['key']>(
    attribute   : ApprovalRequestAttribute,
    key         : Key,
): attribute is Extract<ApprovalRequestAttribute, {key: Key}> => (
    attribute?.key === key
);

const appliedPaymentsShouldShowPrice = (appliedPayments:Types.Apis.Models.AppliedPayments) => (
    !!appliedPayments?.creditCards?.find(cardPayment => cardPayment.amount > 0)
);

const makeApprovalLauncher = ({apis, approveConfirmModal, approval: {approvalGate, approverLevel, id, orders: [{idAgent, idBranch, idCart}]}, session, userLevel, routerHistory}:{
    apis                : Types.Xpresso.Loaders.ContextNameMap['apis'],
    approveConfirmModal : ComponentNext<typeof ApproveConfirmModal>,
    approval            : ApprovalRequest,
    session             : Types.Xpresso.Loaders.ContextNameMap['session'],
    userLevel           : Types.Apis.Models.UserLevel,
    routerHistory       : any
}) => (
    approvalGate === "proof"
        ? async () => {
            await session.applyEmulation({branchId: idBranch, agentId: idAgent === "Branch Order" ? null : idAgent})
            routerHistory.push(`/distribution-options/order/${id}` + buildUrlQuery({
                referrer    : "approval-requests",
                id_cart     : idCart || "new",
            }));
            return {cartId: idCart, submitToNextApprover: false, msg: null};
        }
        : async () => new Promise<{cartId: number, submitToNextApprover: boolean, msg: string}>((resolve, reject) => {
            let cartId:number                   = approvalGate === "cart" ? id : undefined;
            let submitToNextApprover:boolean    = false;
            let msg:string                      = '';
            let success                         = false;
            approveConfirmModal.show({
                approvalDestination : approverLevel === userLevel ? "production" : "the next approver level",
                approvalName        : (approvalGate === "cart" ? `cart ${id}` : `order ${id}`),
                confirm             : async () => {
                    await session.applyEmulation({branchId: idBranch, agentId: idAgent === "Branch Order" ? null : idAgent});
                    const result = (
                        approvalGate === "cart"
                            ? await apis.next.approval.approveCcCart(id)
                            : await apis.next.approval.approveLegacyOrder(id)
                    );

                    if ("cartId" in result) {
                        cartId = Number(result.cartId);
                    }

                    submitToNextApprover = result.submitToNextApprover ?? false;
                    if (submitToNextApprover) {
                        msg = result.message;
                    }
                    success = (
                        (approvalGate === "cart" && (submitToNextApprover || result?.result !== "failure"))
                        || result?.result === "success"
                    );
                    return {
                        success,
                        error   : result?.errors?.msg,
                    };
                },
                onClose             : () => success ? resolve({cartId, submitToNextApprover, msg}) : reject(),
            });
        })
);

type ApprovalRequestOrder = Types.Utils.ArrayItem<ApprovalRequest['orders']>;

/**
 * Cache for API calls common across multiple orders
 * Issues with approval edits staying without reloading react.
 */
// const apiCache = (() => {
//     const cache:{[key: string]: any} = {};

//     return async (key:string, fn:() => any): Promise<any> => {
//         if (!(key in cache)) {
//             cache[key] = fn();
//         }
//         return cache[key];
//     };
// })();

const canApproveRequest = ({approval: {approvalType}}:{
    approval    : ApprovalRequest,
}) => (
    approvalType === "xd"
);

const makeApprovalApproveAttribute = ({apis, approval, approveConfirmModal, session, userLevel, routerHistory}:{
    apis                : Types.Xpresso.Loaders.ContextNameMap['apis'],
    approval            : ApprovalRequest,
    approveConfirmModal : ComponentNext<typeof ApproveConfirmModal>,
    session             : Types.Xpresso.Loaders.ContextNameMap['session'],
    userLevel           : Types.Apis.Models.UserLevel,
    routerHistory       : any,
}) => ({
    key     : "approve" as const,
    label   : approval.approvalGate === "proof" ? "Continue Order" : `Approve ${capitalize(approval.approvalGate)}`,
    run     : makeApprovalLauncher({apis, approval, approveConfirmModal, session, userLevel, routerHistory}),
});

const makeCartOrderDetails = ({appliedCredits, appliedPayments, order, shipping}:{
    appliedCredits  : ApprovalRequest['appliedCredits'],
    appliedPayments : ApprovalRequest['appliedPayments'],
    order           : Types.Apis.Models.CartPendingApproval['orders'][any]
    shipping        : ApprovalRequest['shipping'],
}): Types.Xpresso.Account.Orders.OrderDetailsSidebar.OrderDetails => ({
    item: {
        ...order,
        isFlatRate: Boolean(order.isFlatRate),
        itemId: order.idOrder,
        // blankQty: order.quantity,
        itemName: order.orderName,
        distributionMethod: order.deliveryService,
        thumbnail: order?.itemThumbnails?.[0],
        orderDate: {date: order.dateCreated},
        product: order.orderType,
        components: order.components,
        itemTotals: [
            {
                amount  : order.subTotal,
                name    : "Subtotal",
                type    : "subTotal",
            },
            ...(order.deliveryType === "shipping" ? [{
                amount  : order.shippingCharge,
                name    : "Shipping",
                type    : "shippingCharge"
            }] : []),
            {
                amount  : order.salesTax,
                name    : "Sales tax",
                type    : "salesTax",
            },
            {
                amount  : order.handlingFee,
                name    : "Handling Fee",
                type    : "handlingFee",
            },
            ...(order.rdf > 0 ? [{
                amount      : order.rdf,
                name        : order.rdfDetails.label,
                description : order.rdfDetails.description,
                type        : "rdf"
            }] : []),
            ...(order.vatTax > 0 || order.importDuties > 0 ? [{
                amount      : order.vatTax,
                name        : "VAT/GST",
                type        : "vatTax"
            },
            {
                amount      : order.importDuties,
                name        : "Import Duties",
                type        : "importDuties"
            }] : []),
            // ...(order.importDuties > 0 ? [{
            //     amount      : order.importDuties,
            //     name        : "Import Duties",
            //     type        : "importDuties"
            // }] : []),
        ],
        shipping: order.deliveryType === "shipping" ? {
            ...shipping,
            shippingCharge: order.shippingCharge,
        } : null,
        selections: {
            options:  order.selections.options,
            distributionType: order.deliveryType,
            quantity: order.quantity,
            mailingListIds: order.mailingLists,
            unitPrice: order.unitPrice,
        },
    },
    payments    : mapAppliedPaymentsAsList({appliedCredits, appliedPayments}),
});

const makeDenialLauncher = ({apis, approval, denialConfirmationModal, denialSuccessModal, order}:{
    apis                     : Types.Xpresso.Loaders.ContextNameMap['apis'],
    approval                 : ApprovalRequest,
    denialConfirmationModal  : ComponentNext<typeof DenialConfirmationModal>,
    denialSuccessModal       : ComponentNext<typeof DenialSuccessModal>,
    order                   ?: Types.Utils.ArrayItem<ApprovalRequest['orders']>,
}) => (
    () => new Promise<void>((resolve, reject) => {
        let success = false;
        denialConfirmationModal.show({
            confirm : async () => {
                const result = (
                    !order ? (
                        await apis.next.approval.denyCcCart(approval.id)
                    ) : approval.approvalGate === "cart" ? (
                        await apis.next.approval.denyCcOrder({cartId: order.idCart, itemId: order.idOrder})
                    ) : approval.approvalGate === "proof" ? (
                        await apis.next.approval.denyProofOrder(order.idCart ? {cartId: order.idCart} : {itemId: order.idOrder})
                    ) : (
                        await apis.next.approval.denyLegacyOrder(order.idOrder)
                    )
                );

                if (result?.result === "success") {
                    denialSuccessModal.show({
                        email       : order?.usageEmail ?? approval.orders[0].usageEmail,
                        targetId    : order?.idOrder.toString() ?? approval.id.toString(),
                        targetType  : order ? "Order" : "Cart",
                    });
                    success = true;
                    return {success: true};
                } else {
                    return {success: false, error: result?.errors?.msg};
                }
            },
            onClose: () => success ? resolve() : reject(),
        })
    })
);

const makeOrderEditLauncher = ({approvalGate, company, order, session, routerHistory}:{
    approvalGate    : ApprovalRequest['approvalGate'],
    company         : Types.Xpresso.Loaders.ContextNameMap['company'],
    order           : ApprovalRequest['orders'][number],
    session         : Types.Xpresso.Loaders.ContextNameMap['session'],
    routerHistory   : Types.Xpresso.Loaders.ContextNameMap['routerHistory'],
}) => (
    async () => {
        await session.applyEmulation({
            branchId: order.idBranch,
            agentId: order.idAgent === "Branch Order" ? null : order.idAgent
        });
        if (approvalGate === "proof") {
            routerHistory.push(`/customize/order/${order.idOrder}` + buildUrlQuery({
                referrer: 'approval-requests',
                id_cart: order.idCart ? order.idCart : 'new',
            }));
        } else if ("apm2EventId" in order && order.apm2EventId) {
            window.location.href = `/apm/home?event_id=${order.apm2EventId}&referrer=approval-requests`;
        } else {
            routerHistory.push(`/product-options/order/${order.idOrder}` + buildUrlQuery({
                referrer    : "approval-requests",
                id_cart     : order.idCart
            }));
        }
    }
);

const mapComponents = (components: Types.Apis.Models.OrderPendingApproval['components']): (
    Extract<ApprovalRequestOrderAttribute, {key: "kit"}>['components']
) => (
    Object.entries(components).map(([idTemplate, component]) => ({
        idTemplate: Number(idTemplate),
        ...component,
    }))
);

const mapStandaloneOrderAsDetails = (order:Types.Apis.Models.StandaloneOrderPendingApproval): Types.Xpresso.Account.Orders.OrderDetailsSidebar.OrderDetails => ({
    item: {
        ...order,
        itemId: order.idOrder,
        // blankQty: order.quantity,
        itemName: order.orderName,
        distributionMethod: order.deliveryService,
        isFlatRate: Boolean(order.isFlatRate),
        thumbnail: order.itemThumbnails?.[0],
        orderDate: {date: order.dateCreated},
        product: order.orderType,
        selections: {
            options: order.selections.options,
            distributionType: order.deliveryType,
            mailingListIds: order.mailingLists,
            quantity: order.quantity,
            unitPrice: Number(order.unitPrice),
        },
        itemTotals: [
            {
                amount  : order.subTotal,
                name    : "Subtotal",
                type    : "subTotal",
            },
            ...(order.deliveryType === "shipping" ? [{
                amount  : order.shippingCharge,
                name    : "Shipping",
                type    : "shippingCharge"
            }] : []),
            {
                amount  : order.salesTax,
                name    : "Sales tax",
                type    : "salesTax",
            },
            {
                amount  : order.handlingFee,
                name    : "Handling Fee",
                type    : "handlingFee",
            },
            ...(order.rdf > 0 ? [{
                amount      : order.rdf,
                name        : order.rdfDetails.label,
                description : order.rdfDetails.description,
                type        : "rdf"
            }] : []),
            ...(order.vatTax > 0 || order.importDuties > 0 ? [{
                amount      : order.vatTax,
                name        : "VAT/GST",
                type        : "vatTax"
            },
            {
                amount      : order.importDuties,
                name        : "Import Duties",
                type        : "importDuties"
            }] : []),
        ],
        shipping: {
            shippingAddress: null,
            estimatedDeliveryMessage: null,
            shippingCharge: order.shippingCharge,
            serviceLevel: order.deliveryService,
        },
    },
    payments: (
        Object.keys(order.appliedPayments)
            .filter((paymentType) => order.appliedPayments[paymentType])
            .map((paymentType) => ({
                label: paymentType === "billing" ? order.billing?.name : null,
                type: paymentType.split(/(?=[A-Z])/).map(upperCase1).join(" "),
                value: +order.appliedPayments[paymentType],
                itemId : order.idOrder,
            }))
    )
});

const orderHasComponents = (
    (order: ApprovalRequest['orders'][number]): order is Types.Apis.Models.OrderPendingApproval => (
        "components" in order && order.components !== null
    )
);

